import { createSprinklrEngine } from './sprinklrEngine';
import { emitLiveAgentAnalytics } from './analytics';
import { MessageType, createMessage } from '../shared/message';
import { isImageUrlValid } from '../shared/utils/file-upload-utils';
import { logger } from '../shared/utils/logger';
import {
  LIVE_AGENT_ENDED_MESSAGE,
  LIVE_AGENT_IDLE_DISCONNECTED_MESSAGE,
} from '../shared/constants';
import {
  LIVE_AGENT_ANALYTICS_SURVEY_RECEIVED,
  LIVE_AGENT_ANALYTICS_SURVEY_STARTED,
} from './constants';

/**
 * The LiveAgent block — the module's single entry point, and a NON-HOOK factory (Seq F.c).
 *
 * It composes the Sprinklr engine (`sprinklrEngine.js` — transport + the escalation
 * reconciliation loop) with the handlers that translate live-agent events into Conversation
 * writes, and owns the post-chat survey lifecycle end to end.
 *
 * WHY A PORT. Every one of the six Sprinklr callbacks is "a live-agent event happened → write
 * the Conversation aggregate" (save / update / re-broadcast a message). They used to live in
 * AssistantOrchestrator purely because that is where both aggregates were visible. Naming
 * that dependency as an injected `conversation` port is the actual boundary: this module
 * depends on a small Conversation *interface*, not on the orchestrator.
 *
 * WHY A FACTORY. A hook forces its consumer to instantiate it unconditionally, synchronously,
 * from a static import — so an optional-per-journey block cannot be gated, and neither can a
 * lifecycle-deferred one. A factory can be built conditionally, later, or from a dynamically
 * imported module. `useLiveAgentBridge` is the only React in this module.
 *
 * @param {Object} ports
 * @param {() => Object} ports.getContext  live consumer context, read at FIRE time:
 *   - `{boolean} enabled`   whether the active journey opts into live agent. Membership is
 *     frozen by the bridge at construction; the engine still reads it for its own guards.
 *   - `{Object}  store`     the orchestrator store (escalation, agent flags and
 *     surveyOverlayUrl are published into it).
 *   - `{Object}  conversation`  the Conversation port —
 *     `{ messages, saveMessage, saveMessageIfNew, updateMessage, broadCastMessageUpdate }`.
 *     Read live because `messages` changes every commit and the survey/image handlers search
 *     it; a construction-time snapshot would go stale immediately.
 *   - `{string}  sessionId`, `{number} idleTTL`  passed through to the engine.
 * @param {Object} ports.persistence  the `maa-live-agent` record port — see
 *   createSprinklrEngine, which is its only user.
 * @returns {Object} `init`/`dispose` plus the capability surface the orchestrator puts on the
 *   flow ctx and the assistant context. Note it deliberately does NOT expose the engine's
 *   conversation ids — those were only ever leaked so the caller could build survey
 *   analytics, which this module now owns.
 */
export const createLiveAgent = ({ getContext, persistence }) => {
  const conversation = () => getContext().conversation || {};

  // The six Sprinklr → Conversation handlers, assigned below. Declared as one object rather
  // than named consts because the dependency genuinely runs both ways: the engine calls these
  // handlers, and two of them need the engine's CURRENT conversation ids for analytics. One
  // mutable object resolves that without a forward reference or a lint suppression.
  const handlers = {};

  const engine = createSprinklrEngine({
    getContext: () => {
      const {
        enabled, store, sessionId, idleTTL,
      } = getContext();
      return {
        enabled,
        store,
        sessionId,
        idleTTL,
        ...handlers,
        setEscalated: (value) => store?.getState().setIsEscalated(value),
      };
    },
    persistence,
  });

  // ------ Survey overlay: LiveAgent owns the URL state + the analytics; the Shell owns where
  // the overlay renders (WS-11.1 superseding layer).
  const openSurveyOverlay = (url) => {
    const { store, sessionId } = getContext();
    store.getState().setSurveyOverlayUrl(url);
    emitLiveAgentAnalytics({
      lvQuestion: LIVE_AGENT_ANALYTICS_SURVEY_STARTED,
      sessionId,
      ...engine.getConversationIds(),
    });
  };
  const closeSurveyOverlay = () => getContext().store.getState().setSurveyOverlayUrl(null);

  const onSurveyDismiss = (message) => {
    conversation().updateMessage(message, { surveyExpired: true }, true);
  };

  // ------ Sprinklr → Conversation handlers ------
  // As a hook this file had to split these into `useCallback`s and deliberate plain functions
  // depending on whether the handler needed `messages` fresh. A factory has no render to be
  // stale relative to, so that distinction — and the comment explaining it — disappears.
  handlers.onAgentMessage = async (msg) => {
    await conversation().saveMessageIfNew(msg).catch(() => {});
  };

  // Called by the engine the moment an agent connection is established, by ANY route.
  // Promotion is promote-only and therefore idempotent — the escalation flow also calls it.
  handlers.onAgentConnected = () => {
    const { updateConnectingToConnected, broadCastMessageUpdate } = conversation();
    if (!updateConnectingToConnected) return;
    updateConnectingToConnected()
      .catch(() => {})
      .finally(broadCastMessageUpdate);
  };

  // Called by the engine when Sprinklr signals the conversation has closed.
  handlers.onConversationEnd = (msg) => {
    const { saveMessageIfNew, broadCastMessageUpdate } = conversation();
    saveMessageIfNew({ ...LIVE_AGENT_ENDED_MESSAGE, ...msg })
      .catch(() => {})
      .finally(broadCastMessageUpdate);
  };

  // Called when the survey MESSAGE_RECEIVED event arrives (before or after close). Finds the
  // last ended message and patches it with the surveyUrl. The analytics trigger fires only
  // once the survey is confirmed visible to the user — i.e. the ended message exists and has
  // been updated with the survey URL.
  //
  // ⚠ It matches `variant === 'ended'`, which is the second reason the idle wording keeps its
  // own variant and must not be unified with LIVE_AGENT_ENDED_MESSAGE: the variant gates
  // survey attachment as well as the copy.
  handlers.onSurveyReceived = (surveyUrl) => {
    const { messages, updateMessage, broadCastMessageUpdate } = conversation();
    const endedMsg = messages.findLast(
      (msg) => msg.type === 'system' && msg.variant === 'ended'
    );
    if (endedMsg) {
      updateMessage(endedMsg, { surveyUrl, surveyAppearedAt: Date.now() }, true)
        .catch(() => {})
        .finally(broadCastMessageUpdate);
      emitLiveAgentAnalytics({
        lvQuestion: LIVE_AGENT_ANALYTICS_SURVEY_RECEIVED,
        sessionId: getContext().sessionId,
        ...engine.getConversationIds(),
      });
    }
  };

  // Called by the engine when an image attachment is received (e.g. user echo after upload).
  handlers.onImageReceived = async ({
    url, title, messageId, isAgent, text = '', timestamp,
  }) => {
    const {
      messages, saveMessageIfNew, updateMessage, broadCastMessageUpdate,
    } = conversation();
    const hasValidImageUrl = await isImageUrlValid(url);
    if (!isAgent) {
      // User echo — patch the existing user message with the hosted URL instead of creating a
      // separate message.
      const userMsg = messages.findLast(
        (msg) => msg.type === MessageType.USER && msg.isImageAttached && !msg.storageLink
      );
      if (userMsg) {
        const imageUpdate = hasValidImageUrl ? { storageLink: url } : { imageLoadFailed: true };
        updateMessage(userMsg, imageUpdate, true)
          .catch(() => {})
          .finally(broadCastMessageUpdate);
      }
      return;
    }

    // Agent image: save ONE agent message with the image embedded (image + optional text
    // together), mirroring how user image messages carry their image data. This enables the
    // agent-image path that was previously incomplete (a payload was built but never saved),
    // so the standalone 'image'/'imageFallback' message types are no longer created for new
    // messages.
    const imageFields = hasValidImageUrl
      ? { isImageAttached: true, storageLink: url, imageFileName: title }
      : { isImageAttached: true, imageLoadFailed: true, imageFileName: title };
    saveMessageIfNew(createMessage(MessageType.AGENT, text, {
      messageId,
      timestamp: timestamp || Date.now(),
      ...imageFields,
    }))
      .then((saved) => {
        logger.log('[liveAgent] Agent image', saved ? 'saved:' : 'already saved by a peer tab:', messageId);
      })
      .catch((err) => logger.error('[liveAgent] Failed to save agent image:', err))
      .finally(broadCastMessageUpdate);
  };

  // Called by the engine when idle TTL has expired. ⚠ The wording is deliberately distinct
  // from every other conversation end — same outcome, different copy, and its own variant.
  handlers.onIdleDisconnect = () => {
    const { saveMessage, broadCastMessageUpdate } = conversation();
    saveMessage(LIVE_AGENT_IDLE_DISCONNECTED_MESSAGE)
      .catch(() => {})
      .finally(broadCastMessageUpdate);
  };

  return {
    init: engine.init,
    dispose: engine.dispose,
    // Capabilities the flows dispatch through
    initiateAgentChat: engine.initiateAgentChat,
    sendAgentMessage: engine.sendAgentMessage,
    sendAgentImage: engine.sendAgentImage,
    updateLastActivity: engine.updateLastActivity,
    handleIdleDisconnect: engine.handleIdleDisconnect,
    checkIdleExpired: engine.checkIdleExpired,
    // Survey lifecycle (exposed on the assistant context for SystemMessage + the Shell)
    openSurveyOverlay,
    closeSurveyOverlay,
    onSurveyDismiss,
  };
};
